/**
 * Signage media assets and rotating playlists — types from `@navio/nsdk`.
 */

import type {
  SignageMediaAssetRecord,
  SignageMediaCreateApiData,
  SignageMediaCreateApiResponses,
  SignageMediaCreateInput,
  SignageMediaDeleteApiData,
  SignageMediaDeleteApiResponses,
  SignageMediaGetApiData,
  SignageMediaGetApiResponses,
  SignageMediaListApiData,
  SignageMediaListApiResponses,
  SignageMediaPatchApiData,
  SignageMediaPatchApiResponses,
  SignagePlaylistCreateApiData,
  SignagePlaylistCreateApiResponses,
  SignagePlaylistDeleteApiData,
  SignagePlaylistDeleteApiResponses,
  SignagePlaylistGetApiData,
  SignagePlaylistGetApiResponses,
  SignagePlaylistItemCreateApiData,
  SignagePlaylistItemCreateApiResponses,
  SignagePlaylistItemDeleteApiData,
  SignagePlaylistItemDeleteApiResponses,
  SignagePlaylistItemPatchApiData,
  SignagePlaylistItemPatchApiResponses,
  SignagePlaylistItemType,
  SignagePlaylistListApiData,
  SignagePlaylistListApiResponses,
  SignagePlaylistPatchApiData,
  SignagePlaylistPatchApiResponses
} from '@navio/nsdk';

import { apiUrl, dataEndpoint, responseEndpoint } from '../defineResource';

export type { SignageMediaAssetRecord, SignagePlaylistItemType };

/** Build multipart FormData for media upload (file field cannot go through JSON). */
const mediaCreateFormData = (input: SignageMediaCreateInput): FormData => {
  const formData = new FormData();
  formData.append('name', input.name);
  formData.append('file', input.file);
  if (input.defaultDurationSeconds != null) {
    formData.append('default_duration_seconds', String(input.defaultDurationSeconds));
  }
  if (input.kind != null) {
    formData.append('kind', input.kind);
  }
  if (input.serviceDepartmentId != null) {
    formData.append('service_department_id', String(input.serviceDepartmentId));
  }
  return formData;
};

export const getSignagePlaylistUrls = {
  getPaginatedSignageMedia: dataEndpoint<SignageMediaListApiData, SignageMediaListApiResponses>({
    getUrl: (queryParams) => apiUrl('devices/signage/media/', queryParams)
  }),
  getSignageMedia: dataEndpoint<SignageMediaGetApiData, SignageMediaGetApiResponses>({
    getUrl: ({ pk }) => apiUrl(`devices/signage/media/${pk}/`)
  }),
  postSignageMedia: responseEndpoint<SignageMediaCreateApiData, SignageMediaCreateApiResponses>({
    getUrl: () => apiUrl('devices/signage/media/'),
    getHeaders: () => ({
      'Content-Type': 'multipart/form-data'
    }),
    getBody: (data) => mediaCreateFormData(data),
    skipBodyTransformation: true
  }),
  patchSignageMedia: responseEndpoint<SignageMediaPatchApiData, SignageMediaPatchApiResponses>({
    getUrl: ({ pk }) => apiUrl(`devices/signage/media/${pk}/`),
    getBody: ({ pk: _, ...body }) => body
  }),
  deleteSignageMedia: dataEndpoint<SignageMediaDeleteApiData, SignageMediaDeleteApiResponses>({
    getUrl: ({ pk }) => apiUrl(`devices/signage/media/${pk}/`)
  }),

  getPaginatedSignagePlaylists: dataEndpoint<SignagePlaylistListApiData, SignagePlaylistListApiResponses>({
    getUrl: (queryParams) => apiUrl('devices/signage/playlists/', queryParams)
  }),
  getSignagePlaylist: dataEndpoint<SignagePlaylistGetApiData, SignagePlaylistGetApiResponses>({
    getUrl: ({ pk }) => apiUrl(`devices/signage/playlists/${pk}/`)
  }),
  postSignagePlaylist: responseEndpoint<SignagePlaylistCreateApiData, SignagePlaylistCreateApiResponses>({
    getUrl: () => apiUrl('devices/signage/playlists/'),
    getBody: (data) => data
  }),
  patchSignagePlaylist: responseEndpoint<SignagePlaylistPatchApiData, SignagePlaylistPatchApiResponses>({
    getUrl: ({ pk }) => apiUrl(`devices/signage/playlists/${pk}/`),
    getBody: ({ pk: _, ...body }) => body
  }),
  deleteSignagePlaylist: dataEndpoint<SignagePlaylistDeleteApiData, SignagePlaylistDeleteApiResponses>({
    getUrl: ({ pk }) => apiUrl(`devices/signage/playlists/${pk}/`)
  }),
  postSignagePlaylistItem: responseEndpoint<SignagePlaylistItemCreateApiData, SignagePlaylistItemCreateApiResponses>({
    getUrl: ({ pk }) => apiUrl(`devices/signage/playlists/${pk}/items/`),
    getBody: ({ pk: _, ...body }) => body
  }),
  patchSignagePlaylistItem: responseEndpoint<SignagePlaylistItemPatchApiData, SignagePlaylistItemPatchApiResponses>({
    getUrl: ({ pk, itemId }) => apiUrl(`devices/signage/playlists/${pk}/items/${itemId}/`),
    getBody: ({ pk: _pk, itemId: _itemId, ...body }) => body
  }),
  deleteSignagePlaylistItem: dataEndpoint<SignagePlaylistItemDeleteApiData, SignagePlaylistItemDeleteApiResponses>({
    getUrl: ({ pk, itemId }) => apiUrl(`devices/signage/playlists/${pk}/items/${itemId}/`)
  })
};
